/**
 * OVERRIDE components/manage/Blocks/Listing/getAsyncData.js
 * REASON: Do not load on first render if data is already loaded from SSR to avoid flickering.
 * FILE:
 * FILE VERSION: VLT 5.0.1
 * PULL REQUEST:
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/893
 * DATE: 2025-10-27
 * DEVELOPER: @reebalazs
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */

import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { resolveBlockExtensions } from '@plone/volto/helpers/Extensions';
import qs from 'query-string';
import { slugify } from '@plone/volto/helpers/Utils/Utils';
// START CUSTOMIZATION
import config from '@plone/volto/registry';
import { getAvailableIndexes } from '@fhnw/actions/availableIndexes/availableIndexes';
import { findBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { hasRelevanceTrigger } from '@fhnw/components/Blocks/Listing/filterSorting';
import { listingPageSize } from '@fhnw/components/Blocks/Listing/insertTeaserCard';
import { expandActiveFilters } from '@fhnw/components/Blocks/Listing/mergedFilters';
// END CUSTOMIZATION

const getCurrentPage = (location, id) => {
  const pageQueryParam = qs.parse(location.search);
  switch (Object.keys(pageQueryParam).length) {
    case 0:
      return 1;
    case 1:
      // when there is only one query param, it could be the simple page number or the sluggified block id
      return pageQueryParam['page'] || pageQueryParam[slugify(`page-${id}`)];
    default:
      return pageQueryParam[slugify(`page-${id}`)];
  }
};

// START CUSTOMIZATION
/**
 * Parse URL query parameters to extract active filters for SSR.
 * This mirrors the logic in withFilter.tsx for client-side filter parsing.
 */
const parseActiveFiltersFromURL = (location, data, id, content) => {
  if (!data.activateFilter || !data.filters?.length) {
    return {};
  }

  const parsedParams = qs.parse(location.search);
  const activeFilters = {};

  // Check if there are multiple listing/search blocks to determine if we need postfix
  const blocks = content?.blocks || {};
  const blockTypesWithFilters = ['search', 'listing'];
  const hasMultiple = findBlocks(blocks, blockTypesWithFilters).length > 1;
  const queryStringPostfix = hasMultiple ? slugify(`-${id}`) : '';
  const postfixes = ['', '_from', '_to'];

  data.filters.forEach((filter) => {
    if (!filter.index) return;

    postfixes.forEach((postfix) => {
      const paramKey = `${filter.index}${postfix}${queryStringPostfix}`;
      const value = parsedParams[paramKey];

      if (value !== undefined && value !== null && value !== '') {
        // Ensure array format for filter values
        activeFilters[`${filter.index}${postfix}`] =
          postfix || Array.isArray(value) ? value : [value];
      }
    });
  });

  // Also check for fulltext search
  const fulltextKey = `fulltext${queryStringPostfix}`;
  if (parsedParams[fulltextKey]) {
    activeFilters['fulltext'] = parsedParams[fulltextKey];
  }

  // Check for sort_on from URL (if sortSelection is enabled)
  if (data.sortSelection?.length) {
    const sortOnKey = `sort_on${queryStringPostfix}`;
    if (parsedParams[sortOnKey]) {
      activeFilters['sort_on'] = parsedParams[sortOnKey];
    }
  }

  return activeFilters;
};

const getQueryStringResultsAction = ({
  querystring,
  query,
  sortOn,
  sortOrder,
  subrequestID,
  currentPage,
  dispatch,
  resolvedExtensions,
  path,
  activateFilter,
  showTeaserCard,
}) => {
  const b_size = listingPageSize(
    querystring?.b_size,
    showTeaserCard,
    activateFilter,
    config.settings.defaultPageSize,
  );

  querystring = {
    ...querystring,
    query: query,
    ...(b_size === undefined ? {} : { b_size }),
    // Apply sort_on from URL if present
    ...(sortOn ? { sort_on: sortOn, sort_order: sortOrder } : {}),
  };

  return dispatch(
    getQueryStringResults(
      path,
      {
        ...querystring,
        ...(resolvedExtensions?.variation?.fullobjects
          ? { fullobjects: 1 }
          : { metadata_fields: '_all' }),
      },
      subrequestID,
      currentPage,
      true, // isSSR flag to avoid flickering on first render
    ),
  );
};

export default function getListingBlockAsyncData(props) {
  const { data, path, location, id, dispatch, blocksConfig, content } = props;

  const { resolvedExtensions } = resolveBlockExtensions(data, blocksConfig);

  const subrequestID = content?.UID ? `${content?.UID}-${id}` : id;
  const currentPage = getCurrentPage(location, id);

  if (!data.querystring) {
    return [
      async () => {
        return null;
      },
    ];
  }

  // Parse active filters from URL query parameters
  const activeFilters = parseActiveFiltersFromURL(location, data, id, content);

  let baseQuery = [...(data.querystring?.query || [])];

  // Handle sort_on from URL params (format: "fieldname" or "fieldname_reverse")
  let sortOn = data.querystring?.sort_on;
  let sortOrder = data.querystring?.sort_order || 'ascending';
  if (activeFilters.sort_on) {
    const sortValue = activeFilters.sort_on;
    if (sortValue.endsWith('_reverse')) {
      sortOn = sortValue.replace(/_reverse$/, '');
      sortOrder = 'descending';
    } else {
      sortOn = sortValue;
      sortOrder = 'ascending';
    }
  }

  if (hasRelevanceTrigger(activeFilters)) {
    sortOn = 'relevance';
  }

  // If no filters are activated, simply fetch results
  if (!data.activateFilter) {
    return [
      getQueryStringResultsAction({
        querystring: data.querystring,
        query: baseQuery,
        sortOn: sortOn,
        sortOrder: sortOrder,
        subrequestID: subrequestID,
        currentPage: currentPage,
        dispatch: dispatch,
        resolvedExtensions: resolvedExtensions,
        path: path,
        activateFilter: data.activateFilter,
        showTeaserCard: data.showTeaserCard,
      }),
    ];
  }

  // If filters are activated, fetch available indexes during SSR
  // Build activeFilters for available-indexes subrequest, excluding sort_on
  // to match client-side getActiveFiltersForSubrequest behavior
  const { sort_on: _sortOn, ...activeFiltersForIndexes } = activeFilters;

  return [
    new Promise((resolve) => {
      dispatch(
        getAvailableIndexes(
          path,
          {
            query: baseQuery,
            filters: (data.filters || []).map((filter) => filter.index || ''),
            activeFilters: expandActiveFilters(activeFiltersForIndexes),
          },
          true, // isSSR flag to avoid flickering on first render
        ),
      )
        .then((result) => {
          // Use active query from available-indexes response to fetch final results
          getQueryStringResultsAction({
            querystring: data.querystring,
            query: result.active_query,
            sortOn: sortOn,
            sortOrder: sortOrder,
            subrequestID: subrequestID,
            currentPage: currentPage,
            dispatch: dispatch,
            resolvedExtensions: resolvedExtensions,
            path: path,
            activateFilter: data.activateFilter,
            showTeaserCard: data.showTeaserCard,
          })
            .then(() => resolve())
            .catch(() => {
              resolve();
            });
        })
        .catch(() => resolve());
    }),
  ];
}

// END CUSTOMIZATION
